if (!customElements.get('quick-add-drawer')) {
  customElements.define(
    'quick-add-drawer',
    class QuickAddDrawer extends HTMLElement {
      constructor() {
        super();
        // Query children — safe with defer since DOM is fully parsed before script runs
        this.drawerContent = this.querySelector('[id^="QuickAddDrawerInfo-"]');
        this.overlay = this.querySelector('.quick-add-drawer__overlay');
        this.closeButton = this.querySelector('.quick-add-drawer__close');
        this.openedBy = null;
        this.bindEvents();
      }

      // Move to document.body so position:fixed works correctly,
      // escaping any card overflow:hidden or transform containing blocks.
      // Mirrors the same pattern used by ModalDialog in global.js.
      connectedCallback() {
        if (this.moved) return;
        this.moved = true;
        document.body.appendChild(this);
      }

      bindEvents() {
        if (this.closeButton) {
          this.closeButton.addEventListener('click', this.hide.bind(this));
        }
        if (this.overlay) {
          this.overlay.addEventListener('click', this.hide.bind(this));
        }
        // Also close when clicking the drawer element itself (outside the panel)
        // Mirrors the ModalDialog pattern in global.js
        this.addEventListener('click', (event) => {
          if (event.target === this) this.hide();
        });
        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && this.classList.contains('is-open')) {
            this.hide();
          }
        });
      }

      hide() {
        this.classList.remove('is-open');
        document.body.classList.remove('overflow-hidden-modal');
        // Clear content after transition completes
        setTimeout(() => {
          if (!this.classList.contains('is-open')) {
            const content = this.querySelector('[id^="QuickAddDrawerInfo-"]');
            if (content) content.innerHTML = '';
          }
        }, 450);
        if (this.openedBy) {
          this.openedBy.focus();
          this.openedBy = null;
        }
      }

      show(opener) {
        this.openedBy = opener;

        // Defensive: re-query drawerContent in case constructor ran before children were ready
        if (!this.drawerContent) {
          this.drawerContent = this.querySelector('[id^="QuickAddDrawerInfo-"]');
        }

        if (!this.drawerContent) {
          console.error('[QuickAddDrawer] drawerContent element not found. Drawer HTML may be malformed.');
          return;
        }

        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        const spinner = opener.querySelector('.loading-overlay__spinner');
        if (spinner) spinner.classList.remove('hidden');

        const productUrl = (opener.getAttribute('data-product-url') || '').trim();
        console.log('[QuickAddDrawer] Fetching:', productUrl);

        fetch(productUrl)
          .then((response) => {
            console.log('[QuickAddDrawer] Fetch response status:', response.status);
            return response.text();
          })
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            this.productElement = responseHTML.querySelector('section[id^="MainProduct-"]');

            console.log('[QuickAddDrawer] productElement found:', !!this.productElement);

            if (!this.productElement) {
              console.error('[QuickAddDrawer] Could not find section[id^="MainProduct-"] in fetched HTML. Check that the quickview template renders a main-product section.');
              return;
            }

            this.preventDuplicatedIDs();
            this.removeDOMElements();
            this.setInnerHTML(this.drawerContent, this.productElement.innerHTML);

            console.log('[QuickAddDrawer] Content injected. Opening drawer...');

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            this.preventVariantURLSwitching();
            this.buildImageGallery();

            this.classList.add('is-open');
            document.body.classList.add('overflow-hidden-modal');

            // Focus the panel for accessibility
            const panel = this.querySelector('.quick-add-drawer__panel');
            if (panel) panel.focus();

            console.log('[QuickAddDrawer] Drawer opened successfully.');
          })
          .catch((err) => {
            console.log('[QuickAddDrawer] ERROR during show():', err);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            if (spinner) spinner.classList.add('hidden');
            document.dispatchEvent(new CustomEvent('quickAddContentLoaded', {
              detail: { currentDrawer: this, currentModal: this }
            }));
          });
      }

      // Re-inject script tags so they execute (same as quick-add-modal)
      setInnerHTML(element, html) {
        element.innerHTML = html;
        element.querySelectorAll('script').forEach((oldScriptTag) => {
          const newScriptTag = document.createElement('script');
          Array.from(oldScriptTag.attributes).forEach((attribute) => {
            newScriptTag.setAttribute(attribute.name, attribute.value);
          });
          newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
          oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
        });
      }

      preventVariantURLSwitching() {
        const variantPicker = this.drawerContent.querySelector('variant-radios,variant-selects');
        if (!variantPicker) return;
        variantPicker.setAttribute('data-update-url', 'false');
      }

      removeDOMElements() {
        const pickupAvailability = this.productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = this.productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialogs = this.productElement.querySelectorAll('modal-dialog');
        if (modalDialogs) modalDialogs.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs() {
        const sectionId = this.productElement.dataset.section;
        if (!sectionId) return;
        this.productElement.innerHTML = this.productElement.innerHTML.replaceAll(
          sectionId,
          `quickdrawer-${sectionId}`
        );
        this.productElement
          .querySelectorAll('variant-selects, variant-radios, product-info')
          .forEach((element) => {
            element.dataset.originalSection = sectionId;
          });
      }

      // Extract product images and build the custom gallery
      buildImageGallery() {
        if (!this.drawerContent) return;

        // Collect all product media images from the fetched content
        const mediaItems = this.drawerContent.querySelectorAll(
          '.product__media-item img, .product__media img'
        );
        if (!mediaItems.length) {
          console.log('[QuickAddDrawer] No media images found for gallery.');
          return;
        }

        // Build gallery wrapper
        const galleryWrapper = document.createElement('div');
        galleryWrapper.className = 'qad-gallery-wrapper';

        const gallery = document.createElement('div');
        gallery.className = 'qad-gallery';

        mediaItems.forEach((img) => {
          const item = document.createElement('div');
          item.className = 'qad-gallery__item';

          const clonedImg = img.cloneNode(true);
          // Ensure images load eagerly inside the drawer
          clonedImg.setAttribute('loading', 'eager');
          clonedImg.removeAttribute('srcset');
          // Use the largest available src
          if (clonedImg.dataset.src) {
            clonedImg.src = clonedImg.dataset.src;
          }

          item.appendChild(clonedImg);
          gallery.appendChild(item);
        });

        galleryWrapper.appendChild(gallery);

        // Build pagination dots (visible on mobile only via CSS)
        const dotsContainer = document.createElement('div');
        dotsContainer.className = 'qad-dots';
        mediaItems.forEach((_, i) => {
          const dot = document.createElement('button');
          dot.className = 'qad-dot' + (i === 0 ? ' is-active' : '');
          dot.setAttribute('aria-label', `Go to image ${i + 1}`);
          dot.addEventListener('click', () => {
            const firstItem = gallery.querySelector('.qad-gallery__item');
            if (!firstItem) return;
            const gap = 16; // 1rem gap in px
            gallery.scrollTo({ left: i * (firstItem.offsetWidth + gap), behavior: 'smooth' });
          });
          dotsContainer.appendChild(dot);
        });
        galleryWrapper.appendChild(dotsContainer);

        // Update active dot on scroll
        gallery.addEventListener('scroll', () => {
          const firstItem = gallery.querySelector('.qad-gallery__item');
          if (!firstItem) return;
          const gap = 16;
          const activeIndex = Math.round(gallery.scrollLeft / (firstItem.offsetWidth + gap));
          dotsContainer.querySelectorAll('.qad-dot').forEach((dot, i) => {
            dot.classList.toggle('is-active', i === activeIndex);
          });
        }, { passive: true });

        // Insert gallery before the product info wrapper.
        // Use infoWrapper.parentNode.insertBefore so the reference node is
        // always a direct child of the parent — avoids DOMException: NotFoundError.
        const infoWrapper = this.drawerContent.querySelector('.product__info-wrapper');
        if (infoWrapper && infoWrapper.parentNode) {
          infoWrapper.parentNode.insertBefore(galleryWrapper, infoWrapper);
        } else {
          this.drawerContent.prepend(galleryWrapper);
        }
      }

      // Inject a "View Full Details" link after the product form buttons
      injectViewDetailsLink() {
        if (!this.drawerContent) return;
        // Don't inject if one already exists
        if (this.drawerContent.querySelector('.qad-view-details')) return;

        // Get product URL from the title link
        const titleLink = this.drawerContent.querySelector('.product__title a');
        const productUrl = titleLink ? titleLink.getAttribute('href') : null;
        if (!productUrl) return;

        const viewDetails = document.createElement('a');
        viewDetails.href = productUrl;
        viewDetails.className = 'qad-view-details link underlined-link';
        viewDetails.textContent = 'View Full Details';

        // Insert after the product form buttons
        const formButtons = this.drawerContent.querySelector('.product-form__buttons');
        if (formButtons) {
          formButtons.insertAdjacentElement('afterend', viewDetails);
        } else {
          // Fallback: append to info wrapper
          const infoWrapper = this.drawerContent.querySelector('.product__info-wrapper');
          if (infoWrapper) infoWrapper.appendChild(viewDetails);
        }
      }
    }
  );
}
